import React, { useState, useEffect } from 'react';
import { MessageCircle, X, Send, Minimize2, Maximize2 } from 'lucide-react';
import { featuredProperties } from '../data/properties';
import { infrastructureData } from '../data/infrastructure';
import config from '../utils/config';

const API_URL = config.API_URL;

const AIChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [promptSuggestions] = useState([
    {
      text: "Find houses under ₦5,000,000",
      category: "price"
    },
    {
      text: "Show luxury properties in Lagos",
      category: "property"
    },
    {
      text: "What's the infrastructure like in Lekki?",
      category: "infrastructure"
    },
    {
      text: "Properties near good schools",
      category: "infrastructure"
    },
    {
      text: "Commercial spaces in Abuja",
      category: "property"
    },
    {
      text: "3 bedroom apartments in Victoria Island",
      category: "property"
    }
  ]);
  const [visibleSuggestions, setVisibleSuggestions] = useState([0, 1, 2]);

  const [chatHistory, setChatHistory] = useState([
    {
      type: 'bot',
      content: "Hello! I'm FastFind AI Assistant. How can I help you find your perfect property today?"
    }
  ]);

  // Rotate through prompt suggestions in groups of 3
  useEffect(() => {
    const interval = setInterval(() => {
      setVisibleSuggestions(prev => {
        const nextIndices = prev.map(index => 
          (index + 3) % promptSuggestions.length
        );
        return nextIndices;
      });
    }, 5000); // Change suggestions every 5 seconds

    return () => clearInterval(interval);
  }, [promptSuggestions.length]);

  const processAIResponse = async (userQuery) => {
    setIsTyping(true);
    
    try {
      const propertyContext = featuredProperties.map(p => ({
        title: p.title,
        price: p.price,
        location: p.location,
        beds: p.beds,
        baths: p.baths,
        area: p.area
      }));

      const infrastructureContext = infrastructureData.categories.map(cat => ({
        category: cat.name,
        items: cat.items.map(item => ({
          name: item.name,
          location: item.location,
          distance: item.distance
        }))
      }));

      const messages = chatHistory.map(msg => ({
        role: msg.type === 'user' ? 'user' : 'assistant',
        content: msg.content
      }));

      console.log('Sending request to API with:', {
        messages,
        propertyContext,
        infrastructureContext
      });

      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: [...messages, { role: 'user', content: userQuery }],
          propertyContext,
          infrastructureContext
        }),
      });

      console.log('API Response status:', response.status);
      const data = await response.json();
      console.log('API Response data:', data);

      if (!response.ok) {
        throw new Error(data.error || `API Error: ${response.status}`);
      }

      setTimeout(() => {
        setChatHistory(prev => [...prev, { type: 'bot', content: data.response }]);
        setIsTyping(false);
      }, 1000);

    } catch (error) {
      console.error('Detailed error in processAIResponse:', error);
      setChatHistory(prev => [...prev, {
        type: 'bot',
        content: `I apologize, but I encountered an error: ${error.message}. Please try again.`
      }]);
      setIsTyping(false);
    }
  };

  const handleSend = () => {
    if (message.trim()) {
      // Add user message to chat
      setChatHistory(prev => [...prev, { type: 'user', content: message }]);
      
      // Process the message with AI
      processAIResponse(message);
      
      setMessage('');
    }
  };

  const handleSuggestionClick = (suggestion) => {
    // Add user message to chat
    setChatHistory(prev => [...prev, { type: 'user', content: suggestion.text }]);
    
    // Process the suggestion with AI
    processAIResponse(suggestion.text);
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-[#1c5bde] hover:bg-[#0c0d8a] text-white rounded-full p-4 shadow-lg transition-all duration-200 flex items-center gap-2"
        >
          <MessageCircle className="h-6 w-6" />
          <span className="text-sm">Ask FastFind AI</span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className={`bg-white rounded-lg shadow-xl transition-all duration-200 ${
          isMinimized ? 'h-14' : 'h-[500px]'
        } w-[350px] flex flex-col`}>
          {/* Header */}
          <div className="p-4 bg-[#1c5bde] text-white rounded-t-lg flex justify-between items-center">
            <h3 className="font-semibold">FastFind AI Assistant</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="hover:bg-blue-700 p-1 rounded"
              >
                {isMinimized ? <Maximize2 size={18} /> : <Minimize2 size={18} />}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="hover:bg-blue-700 p-1 rounded"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Chat Content */}
          {!isMinimized && (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {chatHistory.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-3 ${
                        msg.type === 'user'
                          ? 'bg-[#1c5bde] text-white'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {msg.content}
                    </div>
                  </div>
                ))}
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 rounded-lg p-3 max-w-[80%]">
                      <div className="flex gap-2">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Input Area with Multiple Suggestions */}
              <div className="p-4 border-t">
                {/* Clickable Suggestions */}
                <div className="mb-4 space-y-2">
                  {visibleSuggestions.map((index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestionClick(promptSuggestions[index])}
                      className="w-full text-left text-sm text-gray-600 hover:text-[#1c5bde] hover:bg-[#1c5bde]/5 p-2 rounded-lg transition-colors cursor-pointer flex items-center gap-2"
                    >
                      <span className="w-4 h-4">
                        {promptSuggestions[index].category === 'price' && '💰'}
                        {promptSuggestions[index].category === 'property' && '🏠'}
                        {promptSuggestions[index].category === 'infrastructure' && '🏗️'}
                      </span>
                      {promptSuggestions[index].text}
                    </button>
                  ))}
                </div>

                {/* Input Field */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                    placeholder="Type your question..."
                    className="flex-1 border rounded-lg px-3 py-2 focus:outline-none focus:border-blue-600"
                  />
                  <button
                    onClick={handleSend}
                    className="bg-[#1c5bde] hover:bg-[#0c0d8a] text-white rounded-lg px-4 py-2"
                  >
                    <Send size={18} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AIChatWidget; 